import { CollectionRestaurantSummary } from '../core/api/restaurant/restaurant.types';
import { AppConfig } from '../core/api/whitelabel/whitelabel.types';
import { DeliveryOption } from '../core/state/user/user.types';
import { AsyncMap } from '../core/types/data.types';
import { Async } from '../weborder/Data';
import { storage as storageService } from '../weborder/services/storageServices';

// Note: assign, reload, replace are not available via getWindowLocation. It is intended
// as read-only. Please use the dedicated utility function for each of those
// Location functions instead.
type LocationProp =
  | 'ancestorOrigins'
  | 'hash'
  | 'host'
  | 'hostname'
  | 'href'
  | 'origin'
  | 'pathname'
  | 'port'
  | 'protocol'
  | 'search';

const checkIsNewUserSession = (): boolean => {
  const navigationHistoryLength = urlUtils.getUserSessionHistoryLength();
  return navigationHistoryLength === window.history.length;
};

const getCollectionLink = (
  configData: AppConfig,
  collectionSummaries: Async<CollectionRestaurantSummary[]>,
): string => {
  if (!configData) {
    return '/collection';
  }

  if (configData.DisplayPickupRestaurantListScreen) {
    return '/collection';
  }

  if (collectionSummaries.status !== 'success') {
    return '/collection';
  }

  const [skipToStore] = collectionSummaries.data.filter(
    (c) => c.SkipToThisPhysicalRestaurant,
  );

  if (!skipToStore) {
    return '/collection';
  }

  return `/restaurant/${skipToStore.VirtualRestaurantId}/collection/${skipToStore.PhysicalRestaurantId}`;
};

const getDeliveryLocation = (
  deliveryLocationId: number,
  deliveryOptions: AsyncMap<DeliveryOption>,
  preselectedStoreVrid: number,
): string => {
  const option = deliveryOptions[deliveryLocationId];
  const optionData = option && option.status === 'success' && option.data;

  if (preselectedStoreVrid) {
    return `/restaurant/${preselectedStoreVrid}/delivery/${deliveryLocationId}`;
  }

  if (optionData?.type === 'skip') {
    return `/restaurant/${optionData.to.VirtualRestaurantId}/delivery/${deliveryLocationId}`;
  }

  return `/delivery/${deliveryLocationId}`;
};

const getUserSessionHistoryLength = (): number => {
  const onAppLoadUserSessionHistoryLength = storageService.getItem<number>(
    'onAppLoadUserSessionHistoryLength',
  );
  const currentUserSessionHistoryLength = window.history.length + 1; // +1 inclusive for first page visited

  return currentUserSessionHistoryLength - onAppLoadUserSessionHistoryLength;
};

const getWindowLocation = (property?: LocationProp): Location | any => {
  const locationObj = window.location;

  if (property) {
    return locationObj[property];
  }

  return locationObj;
};

const navigateToHash = (hash: string): void => {
  window.location.hash = hash;
};

const navigateToUrl = (url: string): void => {
  window.location.href = url;
};

const replaceUrl = (url: string): void => {
  window.location.replace(url);
};

const assignUrl = (url: string): void => {
  window.location.assign(url);
};

export const urlUtils = {
  assignUrl,
  checkIsNewUserSession,
  getCollectionLink,
  getDeliveryLocation,
  getUserSessionHistoryLength,
  getWindowLocation,
  navigateToHash,
  navigateToUrl,
  replaceUrl,
};
